/**
 * SpinnerWheel Component
 * Canvas-based wheel rendering with spinning animation
 */

import { useSpinnerAudio } from '@/hooks/useSpinnerAudio'
import type { Student } from '@/services/bridge/types/bridge.types'
import { abbreviateName, matchStudent } from '@/utils'
import { useTranslation } from 'react-i18next'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

interface SpinnerWheelProps {
  students: Student[]
  size?: number
  colors?: string[]
  onSelectionComplete?: (student: Student) => void
}

interface SpinnerWheelRef {
  startSpin: (targetStudent?: Student) => void
  getIsSpinning: () => boolean
}

interface SpinState {
  isSpinning: boolean
  currentAngle: number
  targetAngle: number
  startTime: number
  duration: number
}

const DEFAULT_COLORS = ['#B6B6F9', '#9EECED', '#FFE389', '#FF9AAE']

const getLabelFontSize = (count: number): number => {
  if (count <= 10) return 28
  if (count <= 20) return 24
  if (count <= 30) return 20
  if (count <= 40) return 16
  if (count <= 50) return 14
  return 12
}

export const SpinnerWheel = forwardRef<SpinnerWheelRef, SpinnerWheelProps>(
  (
    { students, size = 400, colors = DEFAULT_COLORS, onSelectionComplete },
    ref
  ) => {
    const { t } = useTranslation()
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const animationRef = useRef<number>()
    const [spinState, setSpinState] = useState<SpinState>({
      isSpinning: false,
      currentAngle: 0,
      targetAngle: 0,
      startTime: 0,
      duration: 3000,
    })

    // 整合音效系統 - US-013, US-014
    const { audioControls } = useSpinnerAudio()

    const easeOutCubic = useCallback((t: number): number => {
      const clampedT = Math.max(0, Math.min(1, t))
      const inverted = 1 - clampedT
      const result = 1 - inverted * inverted * inverted
      return clampedT >= 1 ? 1 : result
    }, [])

    const getSegmentInfo = useCallback((studentCount: number) => {
      const anglePerSegment = 360 / studentCount
      const getSegmentCenter = (index: number) =>
        index * anglePerSegment + anglePerSegment / 2
      return { anglePerSegment, getSegmentCenter }
    }, [])

    const selectTargetStudent = useCallback(
      (targetStudent?: Student): { student: Student; index: number } => {
        if (targetStudent) {
          const index = students.findIndex(s => matchStudent(targetStudent, s))
          if (index === -1) {
            throw new Error('Target student not found in students array')
          }
          return { student: targetStudent, index }
        }

        const randomValue = window.crypto?.getRandomValues
          ? window.crypto.getRandomValues(new Uint32Array(1))[0] /
            (0xffffffff + 1)
          : Math.random()

        const randomIndex = Math.floor(randomValue * students.length)
        return { student: students[randomIndex], index: randomIndex }
      },
      [students]
    )

    const calculateTargetAngle = useCallback(
      (targetIndex: number): number => {
        const { getSegmentCenter } = getSegmentInfo(students.length)
        const centerAngle = getSegmentCenter(targetIndex)

        const baseSpins = 5 + Math.floor(Math.random() * 4) // 5-8 full rotations
        const alignmentAngle = 360 - centerAngle
        const offset = (Math.random() - 0.5) * 2 // ±1° random offset

        return (
          spinState.currentAngle + baseSpins * 360 + alignmentAngle + offset
        )
      },
      [getSegmentInfo, students.length, spinState.currentAngle]
    )

    const getSelectedIndex = useCallback(
      (finalAngle: number): number => {
        const { anglePerSegment } = getSegmentInfo(students.length)
        const normalizedAngle = ((finalAngle % 360) + 360) % 360
        const pointerAngleOnWheel = (360 - normalizedAngle) % 360
        return (
          Math.floor(pointerAngleOnWheel / anglePerSegment) % students.length
        )
      },
      [getSegmentInfo, students]
    )

    const startSpin = useCallback(
      (targetStudent?: Student) => {
        if (spinState.isSpinning || students.length === 0) return

        try {
          const { index: targetIndex } = selectTargetStudent(targetStudent)
          const targetAngle = calculateTargetAngle(targetIndex)

          // 修復：動畫時間與音效時間同步 - 音效長度 7.026 秒
          // 設定動畫時間為 6.8-7.2 秒，確保與音效完美同步
          const baseDuration = 6800 // 6.8 秒基礎時間，與音效時間匹配
          const randomVariation = Math.random() * 400 // 隨機變化 0-0.4 秒
          const duration = baseDuration + randomVariation // 最終範圍：6.8-7.2 秒
          const startTime = performance.now()

          setSpinState({
            isSpinning: true,
            currentAngle: spinState.currentAngle,
            targetAngle,
            startTime,
            duration,
          })
        } catch (error) {
          console.error('SpinnerWheel: Failed to start spin', error)
        }
      },
      [spinState, students, selectTargetStudent, calculateTargetAngle]
    )

    useImperativeHandle(
      ref,
      () => ({
        startSpin,
        getIsSpinning: () => spinState.isSpinning,
      }),
      [startSpin, spinState.isSpinning]
    )

    // Core fix: Use ref to store animation parameters, avoiding React state update interference
    const animationParamsRef = useRef<{
      startAngle: number
      targetAngle: number
      startTime: number
      duration: number
    } | null>(null)

    useEffect(() => {
      if (!spinState.isSpinning) {
        animationParamsRef.current = null
        return
      }

      // Set parameters only once at animation start
      if (!animationParamsRef.current) {
        animationParamsRef.current = {
          startAngle: spinState.currentAngle,
          targetAngle: spinState.targetAngle,
          startTime: spinState.startTime,
          duration: spinState.duration,
        }
      }

      const animate = () => {
        const params = animationParamsRef.current
        if (!params) return

        const now = performance.now()
        const elapsed = now - params.startTime
        const progress = elapsed / params.duration

        if (progress >= 1.0) {
          const finalAngle = params.targetAngle
          animationParamsRef.current = null

          // US-014 AC-3: 動畫結束時停止音效
          audioControls.syncSpinSoundWithAnimation(1.0, false)

          setSpinState(prev => ({
            ...prev,
            isSpinning: false,
            currentAngle: finalAngle,
          }))

          const selectedIndex = getSelectedIndex(finalAngle)
          const selectedStudent = students[selectedIndex]

          if (selectedStudent && onSelectionComplete) {
            onSelectionComplete(selectedStudent)
            // 播放勝利音效
            audioControls.playWinnerSound()
          }
          return
        }

        const easedProgress = easeOutCubic(Math.min(progress, 1.0))
        const currentAngle =
          params.startAngle +
          (params.targetAngle - params.startAngle) * easedProgress

        // US-014 AC-2: 同步音效與動畫進度
        audioControls.syncSpinSoundWithAnimation(progress, true)

        setSpinState(prev => ({
          ...prev,
          currentAngle,
        }))

        animationRef.current = requestAnimationFrame(animate)
      }

      animationRef.current = requestAnimationFrame(animate)

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current)
        }
        animationParamsRef.current = null
        // 確保音效停止
        audioControls.stopSpinSound()
      }
      // 修復：只依賴 isSpinning 狀態，避免 audioControls 重新建立導致動畫中斷
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spinState.isSpinning])

    const drawWheel = useCallback(() => {
      const canvas = canvasRef.current
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const actualSize =
        size > 0
          ? size
          : Math.min(canvas.width, canvas.height) /
            (window.devicePixelRatio || 1)
      ctx.clearRect(0, 0, actualSize, actualSize)

      // Parent component handles empty state, but add safety check
      if (students.length === 0) {
        return
      }

      const centerX = actualSize / 2
      const centerY = actualSize / 2
      const radius = actualSize / 2 // Fill the entire canvas
      const { anglePerSegment } = getSegmentInfo(students.length)

      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      const degreesToRadians = (degrees: number) => (degrees * Math.PI) / 180

      // Draw segments
      students.forEach((_student, index) => {
        const baseStartAngleDegrees = index * anglePerSegment
        const baseEndAngleDegrees = (index + 1) * anglePerSegment

        const startAngleDegrees = baseStartAngleDegrees + spinState.currentAngle
        const endAngleDegrees = baseEndAngleDegrees + spinState.currentAngle

        const startAngleRadians = degreesToRadians(startAngleDegrees)
        const endAngleRadians = degreesToRadians(endAngleDegrees)

        ctx.beginPath()
        ctx.moveTo(centerX, centerY)
        ctx.arc(centerX, centerY, radius, startAngleRadians, endAngleRadians)
        ctx.closePath()
        ctx.fillStyle = colors[index % colors.length]
        ctx.fill()
      })

      const studentCount = students.length
      const labelFontSize = getLabelFontSize(studentCount)
      const labelFont = `600 ${labelFontSize}px Inter, system-ui, Avenir, Helvetica, Arial, sans-serif`

      // Draw student names
      students.forEach((student, index) => {
        const segmentCenterDegrees =
          index * anglePerSegment + anglePerSegment / 2
        const textAngleDegrees = segmentCenterDegrees + spinState.currentAngle
        const textAngleRadians = degreesToRadians(textAngleDegrees)

        ctx.save()
        ctx.translate(centerX, centerY)
        ctx.rotate(textAngleRadians)

        // Apply design specifications
        ctx.font = labelFont
        // Right-align so the text's outer end sits at a fixed distance from the
        // circumference; names then extend inward toward the center.
        ctx.textAlign = 'right'
        ctx.textBaseline = 'middle'
        ctx.fillStyle = '#000000' // Black color as per design

        // Anchor the text 16px inside the circumference (its outer edge), and let
        // it grow inward no further than 33% of the radius.
        const EDGE_PADDING = 16
        const textOuterPosition = radius - EDGE_PADDING
        const textInnerBoundary = radius * 0.33

        // VB-649 US 1.5.a: show abbreviated initials on the wheel (the winner
        // reveal in ResultModal still uses the full name).
        const displayText = abbreviateName(student.name)

        // Measure text to handle long names
        const textMetrics = ctx.measureText(displayText)
        const textWidth = textMetrics.width

        // Calculate available space from the outer anchor inward to the boundary
        const availableSpace = textOuterPosition - textInnerBoundary

        // If text is too long, truncate it
        let finalDisplayText = displayText

        if (textWidth > availableSpace) {
          // Text is too long, need to truncate
          let truncatedText = displayText
          const ellipsis = '...'

          // Binary search for the right truncation point
          let low = 0
          let high = displayText.length
          while (low < high) {
            const mid = Math.floor((low + high + 1) / 2)
            truncatedText = displayText.substring(0, mid) + ellipsis
            const truncatedWidth = ctx.measureText(truncatedText).width

            if (truncatedWidth <= availableSpace) {
              low = mid
            } else {
              high = mid - 1
            }
          }

          finalDisplayText = displayText.substring(0, low) + ellipsis
        }

        // Draw the text anchored 16px inside the circumference
        ctx.fillText(finalDisplayText, textOuterPosition, 0)
        ctx.restore()
      })
    }, [students, size, colors, spinState.currentAngle, getSegmentInfo])

    useLayoutEffect(() => {
      const canvas = canvasRef.current
      if (!canvas || size <= 0) return

      const dpr = window.devicePixelRatio || 1
      const scaledSize = Math.round(size * dpr)

      if (canvas.width !== scaledSize || canvas.height !== scaledSize) {
        canvas.width = scaledSize
        canvas.height = scaledSize

        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.scale(dpr, dpr)
        }
      }

      canvas.style.width = `${size}px`
      canvas.style.height = `${size}px`

      // 立即繪製轉盤，避免延遲顯示
      drawWheel()
    }, [size, students, drawWheel])

    return (
      <canvas
        ref={canvasRef}
        dir="ltr"
        style={{
          display: 'block',
          width: size,
          height: size,
        }}
        aria-label={t('spinner.wheelLabel')}
      />
    )
  }
)
